import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface ChurchSocialLinks {
  websiteUrl: string | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  youtubeUrl: string | null;
  xUrl: string | null;
  tiktokUrl: string | null;
}

type SocialFieldKey = keyof ChurchSocialLinks;

const FIELDS: SocialFieldKey[] = [
  'websiteUrl',
  'facebookUrl',
  'instagramUrl',
  'youtubeUrl',
  'xUrl',
  'tiktokUrl',
];

const emptyValues = (): Record<SocialFieldKey, string> => ({
  websiteUrl: '',
  facebookUrl: '',
  instagramUrl: '',
  youtubeUrl: '',
  xUrl: '',
  tiktokUrl: '',
});

export const SocialsPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const socials = useQuery({
    queryKey: ['content', 'socials'],
    queryFn: () => api.get<ChurchSocialLinks>('/content/socials'),
  });

  const [values, setValues] = useState<Record<SocialFieldKey, string>>(emptyValues());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!socials.data) return;
    setValues({
      websiteUrl: socials.data.websiteUrl ?? '',
      facebookUrl: socials.data.facebookUrl ?? '',
      instagramUrl: socials.data.instagramUrl ?? '',
      youtubeUrl: socials.data.youtubeUrl ?? '',
      xUrl: socials.data.xUrl ?? '',
      tiktokUrl: socials.data.tiktokUrl ?? '',
    });
  }, [socials.data]);

  const save = useMutation({
    mutationFn: () => {
      const payload = FIELDS.reduce<ChurchSocialLinks>(
        (acc, key) => {
          acc[key] = values[key].trim() || null;
          return acc;
        },
        {
          websiteUrl: null,
          facebookUrl: null,
          instagramUrl: null,
          youtubeUrl: null,
          xUrl: null,
          tiktokUrl: null,
        },
      );
      return api.patch<ChurchSocialLinks>('/content/socials', payload);
    },
    onSuccess: async (data) => {
      toast.success(t('socials.saved'));
      queryClient.setQueryData(['content', 'socials'], data);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(tErrors(err.code, err.params));
        return;
      }
      setError(tErrors('UNEXPECTED'));
    },
  });

  const isDirty =
    socials.data != null &&
    FIELDS.some((key) => (values[key] || null) !== (socials.data![key] ?? null));

  if (socials.isLoading) {
    return (
      <div>
        <ContentPageHeader />
        <Card className="max-w-3xl p-6">
          <Skeleton className="mb-4 h-10 w-full" />
          <Skeleton className="mb-4 h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </Card>
      </div>
    );
  }

  if (socials.isError) {
    return (
      <div>
        <ContentPageHeader />
        <Card className="max-w-3xl p-6">
          <p className="font-medium text-fg">{tCommon('state.errorTitle')}</p>
          <p className="mt-1 text-body-sm text-fg-secondary">{tCommon('state.errorBody')}</p>
          <div className="mt-4 flex justify-end">
            <Button type="button" variant="secondary" onClick={() => socials.refetch()}>
              {tCommon('action.retry')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <ContentPageHeader />

      <form
        className="max-w-3xl"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Card className="flex flex-col gap-4 p-6">
          {FIELDS.map((key) => (
            <FormField
              key={key}
              label={t(`socials.fields.${key}`)}
              hint={key === 'websiteUrl' ? t('socials.websiteHint') : undefined}
            >
              {(field) => (
                <Input
                  id={field.id}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder={t(`socials.placeholders.${key}`)}
                  value={values[key]}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [key]: event.target.value }))
                  }
                  aria-describedby={field['aria-describedby']}
                  aria-invalid={field.invalid}
                />
              )}
            </FormField>
          ))}

          {error ? <FormFieldError message={error} /> : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!isDirty || save.isPending}
              onClick={() => setValues(emptyValues())}
            >
              {t('socials.clearAll')}
            </Button>
            <Button type="submit" disabled={!isDirty || save.isPending}>
              {save.isPending ? tCommon('form.saving') : tCommon('action.saveChanges')}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
};
